import {
  ACTION_SLOT_ORDER,
  type ActionSlotKey,
} from '@/shared/hooks/useAppPageHeader';
import {
  type BreadcrumbAction,
  type BreadcrumbActionsConfig,
} from '@/shared/contexts/BreadcrumbActionsContext';
import { BiFile, BiPlusCircle, BiPrinter, BiTime } from 'react-icons/bi';
import { Link } from 'react-router-dom';

const SLOT_ICONS: Record<ActionSlotKey, typeof BiPlusCircle> = {
  primary: BiPlusCircle,
  secondary: BiFile,
  tertiary: BiFile,
  print: BiPrinter,
  history: BiTime,
};

function renderActionButton(
  slot: ActionSlotKey,
  action: BreadcrumbAction,
  index: number,
) {
  const Icon = action.icon ?? SLOT_ICONS[slot];
  const btnClass = [
    'app-header-action-bar__btn',
    action.variant === 'primary' && 'app-header-action-bar__btn--primary',
    action.variant === 'outline' && 'app-header-action-bar__btn--outline',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <Icon size={16} aria-hidden />
      <span>{action.label}</span>
    </>
  );

  if (action.dropdownOptions && action.dropdownOptions.length > 0) {
    const dropdownId = `header-action-dropdown-${slot}-${index}`;
    return (
      <div key={index} className="dropdown">
        <button
          type="button"
          className={`${btnClass} dropdown-toggle`}
          id={dropdownId}
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-haspopup="true"
        >
          {content}
        </button>
        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={dropdownId}>
          {action.dropdownOptions.map((opt, i) => (
            <li key={i}>
              <button
                type="button"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  opt.onClick();
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (action.href) {
    return (
      <Link
        key={index}
        to={action.href}
        className={btnClass}
        onClick={action.onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      key={index}
      type="button"
      className={btnClass}
      onClick={(e) => {
        e.preventDefault();
        action.onClick();
      }}
    >
      {content}
    </button>
  );
}

interface AppHeaderActionBarProps {
  hasActions: boolean;
  actions: BreadcrumbActionsConfig;
}

export default function AppHeaderActionBar({
  hasActions,
  actions,
}: AppHeaderActionBarProps) {
  return (
    <div className="app-header-action-bar d-print-none">
      <div className="app-header-action-bar__side app-header-action-bar__side--left" aria-hidden />

      <div className="app-header-action-bar__center">
        {hasActions && (
          <div className="app-header-action-bar__center-inner">
            {ACTION_SLOT_ORDER.map((slot, idx) => {
              const action = actions[slot];
              if (!action) return null;
              return renderActionButton(slot, action, idx);
            })}
          </div>
        )}
      </div>

      <div className="app-header-action-bar__side app-header-action-bar__side--right" aria-hidden />
    </div>
  );
}

